import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FiUserPlus, FiUserX, FiTrash2, FiEdit2 } from 'react-icons/fi';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { unassignAsset, deleteAsset, updateAsset } from '../../api/assets';
import { extractErrorMessage } from '../../api/client';
import { AssetStatus } from '../../enums';
import { useAuth } from '../../auth/AuthContext';
import AssetFormInner from './AssetFormInner';

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm text-ink">{children}</span>
    </div>
  );
}

export default function AssetDetailModal({
  open,
  asset,
  onClose,
  onAssignClick,
}) {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});

  React.useEffect(() => {
    if (asset && isEditing) {
      setEditForm({
        name: asset.name || '',
        category: asset.category || '',
        serialNumber: asset.serialNumber || '',
        assignedTo: asset.assignedTo || '',
        purchaseCost: asset.purchaseCost || '',
        purchaseDate: asset.purchaseDate || '',
        warrantyExpiry: asset.warrantyExpiry || '',
      });
      setEditErrors({});
    }
  }, [asset, isEditing]);

  const updateMut = useMutation({
    mutationFn: (payload) => updateAsset(asset.id, payload),
    onSuccess: () => {
      toast.success('Asset updated');
      qc.invalidateQueries({ queryKey: ['assets'] });
      setIsEditing(false);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const validateEdit = () => {
    const e = {};
    if (!editForm.name.trim()) e.name = 'Required';
    if (!editForm.category.trim()) e.category = 'Required';
    if (!editForm.serialNumber.trim()) e.serialNumber = 'Required';
    if (editForm.purchaseCost === '' || Number(editForm.purchaseCost) < 0)
      e.purchaseCost = 'Must be 0 or greater';
    if (!editForm.purchaseDate) e.purchaseDate = 'Required';
    if (!editForm.warrantyExpiry) e.warrantyExpiry = 'Required';
    if (
      editForm.purchaseDate &&
      editForm.warrantyExpiry &&
      editForm.warrantyExpiry < editForm.purchaseDate
    ) {
      e.warrantyExpiry = 'Must be on or after purchase date';
    }
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveEdit = () => {
    if (!validateEdit()) return;
    const payload = {
      name: editForm.name.trim(),
      category: editForm.category.trim(),
      serialNumber: editForm.serialNumber.trim(),
      purchaseCost: Number(editForm.purchaseCost),
      purchaseDate: editForm.purchaseDate,
      warrantyExpiry: editForm.warrantyExpiry,
    };
    if (editForm.assignedTo) payload.assignedTo = Number(editForm.assignedTo);
    updateMut.mutate(payload);
  };

  const unassignMut = useMutation({
    mutationFn: (id) => unassignAsset(id),
    onSuccess: () => {
      toast.success('Asset unassigned');
      qc.invalidateQueries({ queryKey: ['assets'] });
      setConfirmUnassign(false);
      onClose?.();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteAsset(id),
    onSuccess: () => {
      toast.success('Asset deleted');
      qc.invalidateQueries({ queryKey: ['assets'] });
      setConfirmDelete(false);
      onClose?.();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  if (!asset) return null;

  const isAssigned = asset.status === AssetStatus.ASSIGNED;
  const isAvailable = asset.status === AssetStatus.AVAILABLE;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={asset.name}
        description={`Asset #${asset.id} · ${asset.category || ''}`}
        size="lg"
        footer={
          isEditing ? (
            <div className="flex w-full items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={updateMut.isPending}>
                Cancel
              </Button>
              <Button variant="primary" loading={updateMut.isPending} onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </div>
          ) : isAdmin ? (
            <div className="flex w-full items-center justify-between">
              <Button
                variant="danger"
                icon={FiTrash2}
                onClick={() => setConfirmDelete(true)}
                disabled={deleteMut.isPending}
              >
                Delete
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
                <Button variant="ghost" icon={FiEdit2} onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
                {isAssigned && (
                  <Button
                    variant="secondary"
                    icon={FiUserX}
                    onClick={() => setConfirmUnassign(true)}
                    loading={unassignMut.isPending}
                  >
                    Unassign
                  </Button>
                )}
                {isAvailable && (
                  <Button
                    variant="primary"
                    icon={FiUserPlus}
                    onClick={() => onAssignClick?.(asset)}
                  >
                    Assign
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex w-full items-center justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button variant="ghost" icon={FiEdit2} onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            </div>
          )
        }
      >
        {isEditing ? (
          <AssetFormInner
            form={editForm}
            update={(patch) => setEditForm((p) => ({ ...p, ...patch }))}
            errors={editErrors}
          />
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <StatusPill value={asset.status} />
              {asset.assignedTo != null && (
                <span className="text-xs text-ink-muted">
                  Assigned to {asset.assignedToUsername ? asset.assignedToUsername : `user #${asset.assignedTo}`}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Row label="Name">{asset.name}</Row>
              <Row label="Category">{asset.category || '—'}</Row>
              <Row label="Serial Number">
                <span className="font-mono">{asset.serialNumber || '—'}</span>
              </Row>
              <Row label="Status">
                <StatusPill value={asset.status} />
              </Row>
              <Row label="Purchase Cost">
                <CurrencyDisplay value={asset.purchaseCost} />
              </Row>
              <Row label="Purchase Date">
                <DateDisplay value={asset.purchaseDate} />
              </Row>
              <Row label="Warranty Expiry">
                <DateDisplay value={asset.warrantyExpiry} />
              </Row>
              <Row label="Registered On">
                <DateDisplay value={asset.createdAt} withTime />
              </Row>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmUnassign}
        title="Unassign asset?"
        message={`Remove "${asset.name}" from its current holder? The asset will become available.`}
        confirmLabel="Unassign"
        variant="secondary"
        loading={unassignMut.isPending}
        onConfirm={() => unassignMut.mutate(asset.id)}
        onClose={() => setConfirmUnassign(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete asset?"
        message={`This permanently deletes "${asset.name}". This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate(asset.id)}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}
